export default function AIVoiceSection() {
  const features = [
    { icon: 'ri-24-hours-line', title: '24/7 Availability', desc: 'Never miss a call. AI handles inquiries around the clock without fatigue or downtime.' },
    { icon: 'ri-brain-line', title: 'Natural Conversations', desc: 'Advanced NLP enables human-like dialogue that understands context and intent.' },
    { icon: 'ri-calendar-check-line', title: 'Smart Scheduling', desc: 'Automatically books appointments, sends confirmations, and manages your calendar.' },
    { icon: 'ri-bar-chart-line', title: 'Analytics & Insights', desc: 'Track call metrics, customer sentiment, and conversion rates in real time.' },
    { icon: 'ri-shield-check-line', title: 'Secure & Compliant', desc: 'Enterprise-grade security with HIPAA and TCPA compliance built in.' },
    { icon: 'ri-plug-line', title: 'Easy Integration', desc: 'Connects seamlessly with your CRM, calendar, and existing business tools.' },
  ];

  const steps = [
    { num: '01', title: 'Customer Calls', desc: 'A customer dials your business number and is greeted by your AI voice agent.' },
    { num: '02', title: 'AI Understands', desc: 'The AI processes natural speech, understands intent, and retrieves relevant information.' },
    { num: '03', title: 'Takes Action', desc: 'Books appointments, answers questions, routes calls, or escalates to a human agent.' },
    { num: '04', title: 'Logs & Learns', desc: 'Every interaction is logged, analyzed, and used to continuously improve performance.' },
  ];

  return (
    <section id="ai-voice" className="py-24 px-6 lg:px-12 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-teal-500/20 text-teal-400 text-sm font-semibold rounded-full mb-4 tracking-wide uppercase">
            AI Voice Agents
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl lg:text-5xl font-bold text-white mb-4">
            The Future of Customer Communication
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Replace outdated IVR systems with intelligent AI voice agents that actually understand your customers.
          </p>
          <div className="w-16 h-1 bg-teal-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Comparison: Traditional IVR vs AI Voice Agents */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {/* Traditional IVR */}
          <div className="bg-gray-800/60 rounded-2xl p-8 border border-gray-700">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-red-500/20 rounded-lg">
                <i className="ri-phone-line text-red-400 text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white">Traditional IVR</h3>
            </div>
            <ul className="space-y-4">
              {[
                'Press 1 for sales, Press 2 for support…',
                'Rigid menu trees with no flexibility',
                'Frustrating customer experience',
                'High abandon rates',
                'No learning or improvement over time',
                'Limited to business hours',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-400">
                  <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* AI Voice Agents */}
          <div className="bg-teal-900/40 rounded-2xl p-8 border border-teal-500/40">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-lg">
                <i className="ri-robot-line text-teal-400 text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white">AI Voice Agents</h3>
            </div>
            <ul className="space-y-4">
              {[
                'Natural conversation — just speak normally',
                'Understands context and complex requests',
                'Delightful, human-like experience',
                'Higher engagement and satisfaction',
                'Continuously learns and improves',
                'Available 24/7, 365 days a year',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-400 mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {features.map((f) => (
            <div key={f.title} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-teal-500/50 transition-colors duration-200">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-xl mb-4">
                <i className={`${f.icon} text-teal-400 text-2xl`}></i>
              </div>
              <h4 className="text-white font-semibold text-lg mb-2">{f.title}</h4>
              <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div>
          <h3 className="text-2xl font-bold text-white text-center mb-10">How It Works</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={step.num} className="relative">
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-teal-500/30 z-0" style={{ width: 'calc(100% - 2rem)' }}></div>
                )}
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-500 rounded-full mx-auto mb-4 text-white font-bold text-lg shadow-lg shadow-teal-500/30">
                    {step.num}
                  </div>
                  <h4 className="text-white font-semibold mb-2">{step.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
